import { useLocation, useNavigate } from "@solidjs/router"
import { createEffect, createMemo, createResource, lazy, Match, Suspense, Switch, type JSX } from "solid-js"
import { useLanguage } from "@/context/language"
import { CoworkApiError, listWorkspaces } from "@/cowork/api"
import { CoworkAuthPage } from "@/cowork/auth-page"
import { authLoadingClass } from "@/cowork/auth-shell"
import { activeCompanyID, setActiveCompanyID } from "@/cowork/local-auth"
import { currentWorkspaceSlug, rememberWorkspaceSlug, resolveWorkspaceRoute } from "@/cowork/workspace-route"

const CoworkOnboarding = lazy(() => import("@/pages/cowork-onboarding"))

export function CoworkWorkspaceGate(props: { children: JSX.Element }) {
  const location = useLocation()
  const navigate = useNavigate()
  const language = useLanguage()
  const [workspaces, actions] = createResource(listWorkspaces)
  let rendered = false

  const decision = createMemo(() => {
    if (workspaces.state !== "ready") return
    return resolveWorkspaceRoute({
      pathname: location.pathname,
      search: location.search,
      workspaces: workspaces(),
      activeCompanyId: activeCompanyID(),
      rememberedSlug: currentWorkspaceSlug(),
    })
  })

  createEffect(() => {
    const next = decision()
    if (!next) return
    if (next.workspace) rememberWorkspaceSlug(next.workspace.slug)
    if (next.companyId && next.companyId !== activeCompanyID()) {
      setActiveCompanyID(next.companyId)
      if (rendered && next.kind === "render") {
        window.location.reload()
        return
      }
    }
    if (next.kind === "redirect") navigate(next.to, { replace: true })
  })

  createEffect(() => {
    if (decision()?.kind === "render") rendered = true
  })

  const loading = (
    <div data-cowork-auth class={authLoadingClass}>
      {language.t("cowork.login.loading")}
    </div>
  )

  return (
    <Switch fallback={loading}>
      <Match when={workspaces.error instanceof CoworkApiError && workspaces.error.status === 403}>
        <CoworkAuthPage registration={false} />
      </Match>
      <Match when={workspaces.error}>
        <div data-cowork-auth class={authLoadingClass} role="alert">
          <p>{language.t("cowork.enterprise.scope.loadError")}</p>
          <button type="button" onClick={() => void actions.refetch()}>
            {language.t("cowork.enterprise.scope.retry")}
          </button>
        </div>
      </Match>
      <Match when={decision()?.kind === "onboarding" && decision()}>
        {(current) => (
          <Suspense fallback={loading}>
            <CoworkOnboarding
              workspace={workspaces()!.find((workspace) => workspace.slug === current().workspace!.slug)!}
              onFinished={async (path) => {
                await actions.refetch()
                navigate(path, { replace: true })
              }}
            />
          </Suspense>
        )}
      </Match>
      <Match when={decision()?.kind === "render"}>{props.children}</Match>
    </Switch>
  )
}
